import { Component } from '@theme/component';
import { CartItemsComponent } from '@theme/component-cart-items';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

// Main and sticky buttons share this module, one transaction, and one cart.
let checkoutBusy = false;
let checkoutLeaving = false;
window.addEventListener('pageshow', event => {
  if (!event.persisted) return;
  checkoutBusy = false; checkoutLeaving = false;
  document.documentElement.dataset.annaBuyNowBusy = 'false';
  document.querySelectorAll('[data-anna-buy-now-pending]').forEach(owner => delete owner.dataset.annaBuyNowPending);
  document.dispatchEvent(new CustomEvent('anna:buy-now-state', { detail: { busy: false, phase: 'idle', error: '' } }));
});
let recovery = null;
const pendingCartOperations = new Set();

/** Uses the canonical product form to add once, verify the whole bag, and enter ordinary checkout. */
class AnnaBuyNow extends Component {
  requiredRefs = ['button', 'status'];
  #abort;
  #variantPromise = null;
  #variantGeneration = 0;
  #toastTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    document.addEventListener('click', this.#blockConcurrentPurchase, { signal, capture: true });
    document.addEventListener('anna:buy-now-state', this.#onSharedState, { signal });
    this.#refreshButton();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    clearTimeout(this.#toastTimer);
    // A cart POST is intentionally not aborted or retried when markup is morphed.
  }

  #formComponent() { return this.closest('product-form-component'); }

  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const generation = ++this.#variantGeneration;
    this.#variantPromise = event.promise;
    this.#refreshButton();
    Promise.resolve(event.promise).catch(() => {}).finally(() => {
      if (generation !== this.#variantGeneration || !this.isConnected) return;
      this.#variantPromise = null;
      this.#refreshButton();
    });
  };

  #onCartUpdate = (event) => {
    if (!event.promise || typeof event.promise.then !== 'function') return;
    pendingCartOperations.add(event.promise);
    Promise.resolve(event.promise).catch(() => {}).finally(() => pendingCartOperations.delete(event.promise));
  };

  #blockConcurrentPurchase = (event) => {
    if (!checkoutBusy || !(event.target instanceof Element)) return;
    if (!event.target.closest('[data-anna-buy-now-button], product-form-component [ref="addToCartButton"], sticky-add-to-cart [ref="addToCartButton"]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  #onSharedState = () => this.#refreshButton();

  #refreshButton() {
    const button = this.refs.button;
    if (!button) return;
    const add = this.#formComponent()?.querySelector('add-to-cart-component [ref="addToCartButton"]');
    button.disabled = checkoutBusy || Boolean(this.#variantPromise) || !add || add.disabled;
    button.setAttribute('aria-busy', String(checkoutBusy));
    button.textContent = checkoutBusy ? this.dataset.checkingCopy : this.dataset.buttonCopy;
  }

  #state(phase, busy, error = '') {
    document.documentElement.dataset.annaBuyNowBusy = String(busy);
    document.dispatchEvent(new CustomEvent('anna:buy-now-state', {
      detail: { busy, phase, error, productId: this.dataset.productId },
    }));
  }

  #message(text) {
    this.refs.status.hidden = false;
    this.refs.status.textContent = text;
    if (this.refs.bagLink) this.refs.bagLink.hidden = false;
    const toast = document.getElementById('anna-cart-limit-toast');
    const message = toast?.querySelector('[data-limit-toast-message]');
    if (!toast || !message) return;
    message.textContent = text;
    toast.hidden = false;
    clearTimeout(this.#toastTimer);
    this.#toastTimer = setTimeout(() => { toast.hidden = true; }, 10000);
  }

  #clearMessage() {
    this.refs.status.hidden = true;
    this.refs.status.textContent = '';
    if (this.refs.bagLink) this.refs.bagLink.hidden = true;
  }

  async #readCart() {
    const url = new URL(this.dataset.cartUrl || `${Theme.routes.cart_url}.js`, location.origin);
    url.searchParams.set('_anna_buy_now', String(Date.now()));
    const response = await fetch(url, {
      credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(20000),
    });
    if (!response.ok) throw new Error(this.dataset.checkFailedCopy);
    const cart = await response.json();
    if (!Number.isInteger(cart.item_count) || !Array.isArray(cart.items)) throw new Error(this.dataset.checkFailedCopy);
    return cart;
  }

  async #waitForCart() {
    let timer;
    const timeout = new Promise((_, reject) => { timer = setTimeout(() => reject(new Error(this.dataset.checkFailedCopy)), 25000); });
    const settle = async () => {
      // Repeat because one native update can queue another while it settles.
      while (pendingCartOperations.size) await Promise.allSettled([...pendingCartOperations]);
      while (CartItemsComponent.pendingQuantityUpdates?.length) {
        await Promise.allSettled([...CartItemsComponent.pendingQuantityUpdates]);
      }
    };
    try { await Promise.race([settle(), timeout]); }
    finally { clearTimeout(timer); }
  }

  #selectionFingerprint(data) {
    return JSON.stringify([...data.entries()].filter(([name]) => name === 'id' || name === 'quantity' || name === 'selling_plan' || name.startsWith('properties['))
      .map(([name, value]) => [name, value instanceof File ? `${value.name}:${value.size}:${value.lastModified}` : value])
      .sort((a, b) => String(a[0]).localeCompare(String(b[0]))));
  }

  #matchesLine(item, data) {
    if (String(item.variant_id ?? item.id) !== String(data.get('id'))) return false;
    const plan = String(data.get('selling_plan') || '');
    if (String(item.selling_plan_allocation?.selling_plan?.id || '') !== plan) return false;
    const properties = {};
    for (const [name, value] of data.entries()) {
      if (!name.startsWith('properties[') || !name.endsWith(']') || value === '') continue;
      if (value instanceof File) {
        if (!value.size && !value.name) continue;
        return false; // Uploaded-property URLs cannot safely be inferred after an ambiguous POST.
      }
      properties[name.slice(11, -1)] = value;
    }
    const actual = Object.entries(item.properties || {}).filter(([, value]) => value !== '' && value != null).sort(([a], [b]) => a.localeCompare(b));
    const expected = Object.entries(properties).sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify(actual) === JSON.stringify(expected);
  }

  #lineQuantity(cart, data) {
    return cart.items.reduce((sum, item) => sum + (this.#matchesLine(item, data) ? item.quantity : 0), 0);
  }

  #publishCart(transaction, cart, didError = false) {
    if (!transaction.deferred || transaction.settled) return;
    transaction.settled = true;
    transaction.deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items, source: 'anna-buy-now', sourceId: this.id || this.dataset.productId,
        itemCount: transaction.quantity, productId: this.dataset.productId,
        sections: transaction.response?.sections, didError,
      },
    });
  }

  #goToCheckout() {
    const fallback = Theme.routes.cart_url.replace(/\/cart\/?$/, '/checkout');
    const url = new URL(this.dataset.checkoutUrl || fallback, location.origin);
    if (url.origin !== location.origin || !/\/checkout\/?$/.test(url.pathname)) throw new Error(this.dataset.checkFailedCopy);
    checkoutLeaving = true;
    try { location.assign(url.href); } catch(error) { checkoutLeaving = false; throw error; }
  }

  /** Sticky consumers may call buyNow(), or click the canonical [data-anna-buy-now-button]. */
  handleClick(event) { event?.preventDefault(); return this.buyNow(); }

  async buyNow() {
    if (checkoutBusy || this.#variantPromise) return false;
    const owner = this.#formComponent();
    const sticky = [...(owner?.closest('.shopify-section')?.querySelectorAll('sticky-add-to-cart') || [])]
      .find(candidate => candidate.dataset.productId === owner.dataset.productId);
    if (sticky && typeof sticky.ensurePurchaseOptions !== 'function' && window.matchMedia('(max-width: 749px)').matches) {
      let timer;
      try {
        await Promise.race([
          customElements.whenDefined('sticky-add-to-cart'),
          new Promise(resolve => { timer = setTimeout(resolve, 2500); }),
        ]);
      } finally { clearTimeout(timer); }
      // Do not purchase a server-default mobile variant before the shared picker gate has loaded.
      if (typeof sticky.ensurePurchaseOptions !== 'function') return false;
    }
    if (checkoutBusy || this.#variantPromise || (sticky?.ensurePurchaseOptions && !sticky.ensurePurchaseOptions())) return false;
    const form = owner?.querySelector('form');
    const add = owner?.querySelector('add-to-cart-component [ref="addToCartButton"]');
    if (!(form instanceof HTMLFormElement) || !add || add.disabled || !form.reportValidity()) return false;
    const quantityCheck = owner.refs?.quantitySelector?.canAddToCart?.();
    if (quantityCheck && !quantityCheck.canAdd) { this.#message(this.dataset.quantityFailedCopy); return false; }
    const data = new FormData(form);
    const quantity = Number(data.get('quantity') || owner.dataset.quantityDefault || 1);
    if (!/^\d+$/.test(String(data.get('id') || '')) || !Number.isInteger(quantity) || quantity <= 0) {
      this.#message(this.dataset.quantityFailedCopy); return false;
    }
    data.set('quantity', String(quantity));
    const fingerprint = this.#selectionFingerprint(data);
    const limit = Number(this.dataset.limitMax) || 5;
    checkoutBusy = true;
    owner.dataset.annaBuyNowPending = 'true';
    this.#clearMessage();
    this.#state('checking', true);
    let transaction = recovery;
    try {
      await this.#waitForCart();
      let cart = await this.#readCart();
      if (transaction) {
        // After a confirmed/ambiguous POST we NEVER silently submit a second add.
        if (transaction.fingerprint !== fingerprint) throw new Error(this.dataset.reviewBagCopy);
        if (transaction.phase === 'unknown') {
          if (this.#lineQuantity(cart, transaction.data) < transaction.baseline + transaction.quantity) throw new Error(this.dataset.reviewBagCopy);
          transaction.phase = 'added';
        }
      } else {
        if (cart.item_count + quantity > limit) throw new Error(this.dataset.limitCopy);
        const sectionIds = [...document.querySelectorAll('cart-items-component')].map(item => item.dataset.sectionId).filter(Boolean);
        if (sectionIds.length) data.set('sections', [...new Set(sectionIds)].join(','));
        transaction = { fingerprint, data, quantity, phase: 'adding', baseline: this.#lineQuantity(cart, data), response: null, deferred: CartLinesUpdateEvent.createPromise(), settled: false };
        recovery = transaction;
        transaction.deferred.promise.catch(() => {});
        owner.dispatchEvent(new CartLinesUpdateEvent({
          action: 'add', context: 'product', lines: [{ merchandiseId: String(data.get('id')), quantity }],
          promise: transaction.deferred.promise,
        }));
        this.#state('adding', true);
        let response;
        let result;
        try {
          response = await fetch(this.dataset.addUrl || Theme.routes.cart_add_url, {
            method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' },
            body: data, signal: AbortSignal.timeout(25000),
          });
          result = await response.json();
        } catch {
          transaction.phase = 'unknown';
          throw new Error(this.dataset.reviewBagCopy);
        }
        if (!response.ok || (Number(result.status) >= 400)) {
          // An inventory error may still leave a partially added quantity. Only release the
          // recovery guard after a fresh cart proves no units were added to this selection.
          let verifiedFailureCart = null;
          try { verifiedFailureCart = await this.#readCart(); } catch {}
          if (response.status >= 400 && response.status < 500 && verifiedFailureCart &&
              this.#lineQuantity(verifiedFailureCart, data) === transaction.baseline) recovery = null;
          else transaction.phase = 'unknown';
          this.#publishCart(transaction, verifiedFailureCart || cart, true);
          throw new Error(typeof result.description === 'string' ? result.description : result.message || this.dataset.checkFailedCopy);
        }
        transaction.response = result;
        transaction.phase = 'added';
        this.#state('confirming', true);
        cart = await this.#readCart();
      }
      this.#publishCart(transaction, cart);
      if (cart.item_count < 1 || cart.item_count > limit) throw new Error(this.dataset.limitCopy);
      if (this.#lineQuantity(cart, transaction.data) < transaction.baseline + transaction.quantity) throw new Error(this.dataset.reviewBagCopy);
      this.#state('checkout', true);
      this.#goToCheckout();
      return true;
    } catch (error) {
      if (transaction?.deferred && !transaction.settled) {
        transaction.settled = true;
        transaction.deferred.reject(error);
      }
      this.#message(error?.message || this.dataset.checkFailedCopy);
      this.#state('error', true, error?.message || '');
      return false;
    } finally {
      if (!checkoutLeaving) {
        checkoutBusy = false;
        delete owner.dataset.annaBuyNowPending;
        this.#state('idle', false);
        this.#refreshButton();
      }
    }
  }
}

if (!customElements.get('anna-buy-now')) customElements.define('anna-buy-now', AnnaBuyNow);
